import React, { useState, useEffect, useRef } from "react"

import appTheme from "appTheme"
import useTracking from "hooks/useTracking"
import slugify from "utils/slugify"

import {
  Container,
  Line,
  StyledCarousel,
  Slider,
  StyledSlide,
  Content,
  Category,
  Text,
  Separator,
  ArrowLeft,
  ArrowRight,
} from "./styles"

const ArticleGallery = ({ items, pageType, tracking, trackingId }) => {
  if (items?.length === 0) {
    return null
  }

  const { breakpoints } = appTheme
  const totalSlides = items.length
  const [visibleSlides, setVisibleSlides] = useState(Math.min(5, totalSlides))
  const [currentSlide, setCurrentSlide] = useState(0)

  useEffect(() => {
    const { mobile, tablet, hamburgerLarge, desktopL } = breakpoints

    const handleResize = () => {
      const width =
        window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
      setVisibleSlides(
        width < mobile
          ? Math.min(1, totalSlides)
          : width < tablet
          ? Math.min(2, totalSlides)
          : width < hamburgerLarge
          ? Math.min(3, totalSlides)
          : width < desktopL
          ? Math.min(4, totalSlides)
          : Math.min(5, totalSlides),
      )
    }

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(document.body)

    return () => {
      resizeObserver.unobserve(document.body)
    }
  }, [breakpoints, totalSlides])

  const changeSlide = (event, value) => {
    if (event.type === "keydown") {
      if (event.key === "ArrowLeft" && currentSlide > 0) {
        setCurrentSlide((prev) => prev - 1)
      } else if (event.key === "ArrowRight" && currentSlide < totalSlides - 1) {
        setCurrentSlide((prev) => prev + 1)
      }
    } else if (event.type === "click") {
      setCurrentSlide((prev) => prev + value)
    }
  }

  const { trackRender, trackView, trackClick } = useTracking()
  const ref = useRef()

  if (tracking && tracking.includes("Render")) {
    trackRender(ref, trackingId, { ...(pageType && { page_type: pageType }) })
  }

  if (tracking && tracking.includes("View")) {
    trackView(ref, trackingId, { ...(pageType && { page_type: pageType }) })
  }

  if (tracking && tracking.includes("Render")) {
    trackClick(ref, trackingId, { ...(pageType && { page_type: pageType }) })
  }

  return (
    <Container ref={ref}>
      <Line />
      <StyledCarousel
        totalSlides={items.length}
        visibleSlides={visibleSlides}
        onKeyDown={changeSlide}
      >
        <Slider>
          {items.map((item, idx) => (
            <StyledSlide key={item.itemId} index={idx}>
              <Card {...item} pageType={pageType} />
              <Separator isLastVisible={idx + 1 === currentSlide + visibleSlides} />
            </StyledSlide>
          ))}
        </Slider>
        <ArrowLeft onClick={(event) => changeSlide(event, -1)} tabIndex={-1} />
        <ArrowRight onClick={(event) => changeSlide(event, 1)} tabIndex={-1} />
      </StyledCarousel>
      <Line />
    </Container>
  )
}

const Card = ({ article, pageType, tracking, trackingId }) => {
  if (article === null) {
    return null
  }

  const { category, id, partner, sponsor, subcategory, titles, url } = article
  const { trackRender, trackView, trackClick } = useTracking()
  const ref = useRef()

  const collaborator = partner || sponsor
  const trackId =
    trackingId || (partner ? "partner_article" : sponsor ? "sponsor_article" : `article-${id}`)
  const extraProps = {
    ...(pageType && { page_type: pageType }),
    click_title: titles.site || titles.body,
    click_url: `/articles/${url}`,
    article_id: id,
    ...(collaborator &&
      (partner ? { partner_id: collaborator.partnerId } : { sponsor_id: collaborator.sponsorId })),
    category: "Content Engagement",
    content_category: category,
    content_subcategory: subcategory,
  }

  if (tracking && tracking.includes("Render")) {
    trackRender(ref, trackId, extraProps)
  }

  if (tracking && tracking.includes("View")) {
    trackView(ref, trackId, extraProps)
  }

  if (tracking && tracking.includes("Click")) {
    trackClick(ref, trackId, extraProps)
  }

  const renderTop = () => {
    const partnershipType = partner ? "Partner" : sponsor ? "Sponsor" : null
    if (partnershipType) {
      return (
        <Category
          {...(collaborator.url && {
            to: `/partner/${collaborator.url}`,
            title: partner.name,
          })}
          tabIndex={-1}
        >
          {partnershipType === "Sponsor" ? "Sponsor | " : "Paid Content | "} {collaborator.name}
        </Category>
      )
    }

    return (
      <Category
        dangerouslySetInnerHTML={{ __html: subcategory || category }}
        to={`/${slugify(subcategory)}`}
        title={subcategory}
        tabIndex={-1}
      />
    )
  }

  return (
    <Content>
      {renderTop(article)}
      <Text
        ref={ref}
        dangerouslySetInnerHTML={{ __html: article.titles.site || article.titles.body }}
        to={`/articles/${article.url}`}
        title={article.titles.site || article.titles.body}
      />
    </Content>
  )
}

export default ArticleGallery
